'use client';

import { c as _c } from "react/compiler-runtime";
import * as z from 'zod';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.ts';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.ts';
import { trackSelectContent } from '@sola/tracking/components/datalayer.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import { CardList } from '../card-list/card-list.tsx';
import { ArticleCardSkeleton, Card } from '../card/card.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { Pagination } from '../pagination/pagination.tsx';
import { getEyebrow, useArticleList } from './article-list.logic.ts';
const queryClient = new QueryClient();
export const ArticleListProps = z.object({
  seoLevel: z.number().int().optional(),
  articleType: ArticleTypesEnum.optional(),
  country: z.string().optional(),
  theme: z.string().optional(),
  topic: z.string().optional(),
  limit: z.number().int().optional(),
  onArticleLoad: z.function({
    input: [z.object({
      totalCount: z.number()
    })]
  }).optional(),
  variant: z.enum(['default', 'related']).optional(),
  excludeContentfulEntryID: z.string().optional(),
  hidePagination: z.boolean().optional(),
  /** When true, filters are read from URL params (dynamic). When false, uses static CMS props. */
  isDynamic: z.boolean().optional()
});
export type ArticleListProps = z.infer<typeof ArticleListProps>;
const ArticleListSkeleton: React.FC<{
  itemCount?: number;
  columns?: number;
  columnsMd?: number;
  columnsLg?: number;
}> = t0 => {
  const $ = _c(7);
  const {
    itemCount: t1,
    columns: t2,
    columnsMd: t3,
    columnsLg: t4
  } = t0;
  const itemCount = t1 === undefined ? 12 : t1;
  const columns = t2 === undefined ? 1 : t2;
  const columnsMd = t3 === undefined ? 3 : t3;
  const columnsLg = t4 === undefined ? 4 : t4;
  let t5;
  if ($[0] !== itemCount) {
    t5 = Array.from({
      length: itemCount
    }, _temp);
    $[0] = itemCount;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== columns || $[3] !== columnsLg || $[4] !== columnsMd || $[5] !== t5) {
    t6 = <CardList columns={columns} columnsMd={columnsMd} columnsLg={columnsLg} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{t5}</CardList>;
    $[2] = columns;
    $[3] = columnsLg;
    $[4] = columnsMd;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
const InternalArticleList: React.FC<ArticleListProps> = t0 => {
  const $ = _c(51);
  let onArticleLoad;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      seoLevel: t1,
      onArticleLoad,
      variant: t2,
      isDynamic: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onArticleLoad;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    onArticleLoad = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const seoLevel = t1 === undefined ? 3 : t1;
  const variant = t2 === undefined ? "default" : t2;
  const isDynamic = t3 === undefined ? false : t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["articles", "skippy"];
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    t
  } = useTranslation(t4);
  const listRef = useRef(null);
  let t5;
  if ($[7] !== isDynamic || $[8] !== onArticleLoad || $[9] !== props || $[10] !== variant) {
    t5 = {
      ...props,
      variant,
      isDynamic,
      onArticleLoad
    };
    $[7] = isDynamic;
    $[8] = onArticleLoad;
    $[9] = props;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    articles,
    articleData,
    isLoading,
    isError,
    resolvedLimit,
    columnLayout,
    shouldShowPagination,
    articleType
  } = useArticleList(t5);
  if (isLoading) {
    let t6;
    if ($[12] !== columnLayout || $[13] !== resolvedLimit) {
      t6 = <ArticleListSkeleton itemCount={resolvedLimit} {...columnLayout} />;
      $[12] = columnLayout;
      $[13] = resolvedLimit;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  if (isError) {
    let t6;
    if ($[15] !== t) {
      t6 = t("skippy:articles.errorLoading", "Error loading articles. Please try again.");
      $[15] = t;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t6) {
      t7 = <div className="ArticleList flex flex-col gap-comfy-sm"><div className="text-center p-comfy-xl"><Alert variant="negative" message={t6} /></div></div>;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  if (!articles?.length) {
    let t6;
    if ($[19] !== t) {
      t6 = t("articles:noResultsTitle");
      $[19] = t;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <p />;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    const t8 = `${articlesUrlBase[articleType as ArticleTypesEnum]}?tab=theme`;
    let t9;
    if ($[22] !== t8) {
      t9 = <Link href={t8} />;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    const t10 = `${articlesUrlBase[articleType as ArticleTypesEnum]}?tab=search`;
    let t11;
    if ($[24] !== t10) {
      t11 = <Link href={t10} />;
      $[24] = t10;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== t11 || $[27] !== t9) {
      t12 = <IntlBlock i18nKey="articles:noResultsBody" components={{
        1: t7,
        2: t9,
        3: t11
      }} />;
      $[26] = t11;
      $[27] = t9;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== t12 || $[30] !== t6) {
      t13 = <div className="ArticleList flex flex-col gap-comfy-sm"><FeatureBase title={t6} body={t12} /></div>;
      $[29] = t12;
      $[30] = t6;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    return t13;
  }
  let t6;
  if ($[32] !== articleType || $[33] !== articles || $[34] !== seoLevel || $[35] !== t) {
    let t7;
    if ($[37] !== articleType || $[38] !== seoLevel || $[39] !== t) {
      t7 = article => {
        const cardProps = extractArgsSearchResult(article);
        return <Card key={article.contentfulEntryID} {...getBaseSizeProps(4)} seoLevel={seoLevel} title={article.pageTitle} subtitle={getEyebrow(cardProps, articleType, t)} media={cardProps.media} body={<p>{article.summary}</p>} primaryCTA={article.path ? {
          link: article.path,
          title: "Read more"
        } : undefined} variant="post" hasPlaceholder={true} alt={true} onClick={() => trackSelectContent(article.contentfulEntryID, article.pageTitle, article.contentfulType, [...(article.taxonomy?.articleTopics ?? []), ...(article.taxonomy?.articleThemes ?? [])])} />;
      };
      $[37] = articleType;
      $[38] = seoLevel;
      $[39] = t;
      $[40] = t7;
    } else {
      t7 = $[40];
    }
    t6 = articles.map(t7);
    $[32] = articleType;
    $[33] = articles;
    $[34] = seoLevel;
    $[35] = t;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[41] !== columnLayout || $[42] !== t6) {
    t7 = <CardList {...columnLayout} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{t6}</CardList>;
    $[41] = columnLayout;
    $[42] = t6;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== articleData || $[45] !== resolvedLimit || $[46] !== shouldShowPagination) {
    t8 = shouldShowPagination && articleData?.totalCount && <Pagination itemsPerPage={resolvedLimit} totalItems={articleData.totalCount} scrollRef={listRef} />;
    $[44] = articleData;
    $[45] = resolvedLimit;
    $[46] = shouldShowPagination;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  let t9;
  if ($[48] !== t7 || $[49] !== t8) {
    t9 = <div ref={listRef} className="ArticleList flex flex-col gap-comfy-sm">{t7}{t8}</div>;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  return t9;
};
export const ArticleList: React.FC<ArticleListProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ArticleListSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}><InternalArticleList {...props} /></QueryClientProvider></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(_, index) {
  return <ArticleCardSkeleton key={`loading-article-skeleton-${index}`} />;
}